'use client';

import { useState, useEffect } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '@/lib/firebase';

export interface CollectionEcosystemContext {
  tvl: number;
  tvlChange24h: number;
  dexVolume24h?: number;
  protocolCount: number;
  dataTimestamp?: string;
}

export interface CollectionPulse {
  summary: string;
  timestamp: string;
  generatedBy: string;
  metrics: {
    totalCollections: number;
    activeCollections: number;
    topByVolume: {
      slug: string;
      volume: number;
    } | null;
    topByFloor: {
      slug: string;
      floorPrice: number;
    } | null;
    topBySales: {
      slug: string;
      salesCount: number;
    } | null;
    averageVolume: number;
    totalVolume: number;
    averageFloorChange: number;
    volatilityLevel: string;
    top3Concentration?: number;
  };
  ecosystemContext?: CollectionEcosystemContext | null;
}

/**
 * Factual metrics for NFT collection (no fake indicators!)
 */
export interface CollectionMetrics {
  whaleActivityRatio: number;      // avg sale / floor price
  volumePerSale: number;            // ETH per sale
  ownershipConcentration: number;  // owners / supply
  volumeRank: number;               // #1, #2, #3, etc.
  salesRank: number;                // #1, #2, #3, etc.
  floorRank: number;                // #1, #2, #3, etc.
  volumeShare: number;              // % of ecosystem volume
}

export interface CollectionInsight {
  analysis: string;
  metrics: CollectionMetrics;
  timestamp: string;
  generatedBy: string;
  blockchain?: string; // 'ethereum' or 'abstract'
  rawData?: {
    floorPrice: number;
    volume24h: number;
    salesCount: number;
    averagePrice: number;
    whaleActivityRatio: number;
    volumeRank: number;
    volumeShare: number;
  };
  ecosystemContext?: {
    tvl: number;
    tvlChange24h: number;
    protocolCount: number;
  } | null;
  /**
   * The entity behind the collection (stamped by the backend insight pipeline when
   * a tracked handle/tag matches) — `entitySlug` is the dossier door.
   */
  socialContext?: {
    entityName?: string;
    entitySlug?: string;
    handleId?: string;
    sentiment?: string;
    username?: string;
  } | null;
}

export interface CollectionInsights {
  [slug: string]: CollectionInsight;
}

interface UseCollectionAnalysisReturn {
  pulse: CollectionPulse | null;
  insights: CollectionInsights | null;
  loading: boolean;
  error: string | null;
  lastUpdated: Date | null;
}

/**
 * Hook to fetch AI-generated collection analysis from Firestore
 * Listens for real-time updates
 */
export function useCollectionAnalysis(): UseCollectionAnalysisReturn {
  const [pulse, setPulse] = useState<CollectionPulse | null>(null);
  const [insights, setInsights] = useState<CollectionInsights | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

  useEffect(() => {
    // Skip during build time or when Firebase is not available
    if (!db || typeof window === 'undefined') {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Subscribe to collection pulse
      const pulseUnsubscribe = onSnapshot(
        doc(db, 'collection_analysis', 'pulse'),
        (snapshot) => {
          if (snapshot.exists()) {
            const data = snapshot.data() as CollectionPulse;
            setPulse(data);
            setLastUpdated(new Date());
            setError(null);
          } else {
            setPulse(null);
          }
          setLoading(false);
        },
        (err) => {
          console.warn('Collection pulse listener error:', err);
          setError('Failed to fetch collection pulse');
          setLoading(false);
        }
      );

      // Subscribe to collection insights
      const insightsUnsubscribe = onSnapshot(
        doc(db, 'collection_analysis', 'insights'),
        (snapshot) => {
          if (snapshot.exists()) {
            const data = snapshot.data() as CollectionInsights;
            setInsights(data);
            setLastUpdated(new Date());
            setError(null);
          } else {
            setInsights(null);
          }
          setLoading(false);
        },
        (err) => {
          console.warn('Collection insights listener error:', err);
          setError('Failed to fetch collection insights');
          setLoading(false);
        }
      );

      // Cleanup listeners on unmount
      return () => {
        pulseUnsubscribe();
        insightsUnsubscribe();
      };
    } catch (err) {
      console.warn('Failed to initialize collection analysis listeners:', err);
      setError('Failed to initialize collection analysis');
      setLoading(false);
    }
  }, []);

  return {
    pulse,
    insights,
    loading,
    error,
    lastUpdated
  };
}

/**
 * Helper function to get insight for a specific collection
 * Tries multiple slug variations to match Firestore keys
 */
export function getCollectionInsight(insights: CollectionInsights | null, slug: string): CollectionInsight | null {
  if (!insights) return null;

  const slugVariations = [
    slug.toLowerCase(),                           // lil-pudgys
    slug.toLowerCase().replace(/\s+/g, '-'),     // lil-pudgys (if spaces)
    slug.toLowerCase().replace(/-/g, ''),        // lilpudgys (no hyphens)
    slug.toLowerCase().replace(/-/g, '_'),       // lil_pudgys (underscores)
  ];

  // Try each variation
  for (const variation of slugVariations) {
    if (insights[variation]) {
      return insights[variation];
    }
  }

  return null;
}

/**
 * Helper function to format time ago
 */
export function getTimeAgo(timestamp: string): string {
  const now = new Date();
  const then = new Date(timestamp);
  const diffMs = now.getTime() - then.getTime();
  const diffMins = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;

  const diffDays = Math.floor(diffHours / 24);
  if (diffDays === 1) return 'Yesterday';
  return `${diffDays}d ago`;
}

/**
 * Helper function to check if data is stale (older than 12 hours)
 */
export function isDataStale(timestamp: string, maxAgeHours: number = 12): boolean {
  const now = new Date();
  const then = new Date(timestamp);
  const diffHours = (now.getTime() - then.getTime()) / (1000 * 60 * 60);
  return diffHours > maxAgeHours;
}

